import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stepper,
  Step,
  StepLabel,
  Box,
  Typography,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  TextField,
  Select,
  MenuItem,
  InputLabel,
  Chip,
  CircularProgress,
  Alert,
  Autocomplete,
  Snackbar,
  Checkbox,
  ListItemText,
  OutlinedInput,
  InputAdornment,
} from '@mui/material';
import { Facebook as MetaIcon, CheckCircle as SuccessIcon } from '@mui/icons-material';
import { apiService, type MetaPerformanceGoal } from '../services/api';
import { getFrontendAppDeployment, type FrontendAppDeployment } from '../utils/appDeployment';

// Types
interface Campaign {
  id: string;
  name: string;
  status: string;
  objective?: string;
}

interface AdSet {
  id: string;
  name: string;
  status: string;
  campaign_id: string;
}

interface PrimaryTextOption {
  id: string;
  text: string;
  flow?: string;
}

interface LocationOption {
  name: string;
  code: string;
}

interface Flow {
  id: string;
  name: string;
  facebook_page_id: string;
  instagram_account_id: string;
  url: string;
  primary_texts: Array<{ id: string; text: string }>;
}

interface CreateMetaAdDialogProps {
  open: boolean;
  onClose: () => void;
  selectedAdIds: string[];
}

const DEFAULT_PERFORMANCE_GOAL: MetaPerformanceGoal = 'OFFSITE_CONVERSIONS';

const PERFORMANCE_GOAL_OPTIONS: Array<{
  value: MetaPerformanceGoal;
  label: string;
  description: string;
}> = [
  {
    value: 'OFFSITE_CONVERSIONS',
    label: 'Maximise number of conversions',
    description: 'Show ads to people most likely to take the selected conversion action.',
  },
  {
    value: 'VALUE',
    label: 'Maximise value of conversions',
    description: 'Show ads to people most likely to generate higher conversion value.',
  },
];

const getPerformanceGoalLabel = (value: MetaPerformanceGoal): string =>
  PERFORMANCE_GOAL_OPTIONS.find((option) => option.value === value)?.label || value;

const APP_FLOWS: Record<FrontendAppDeployment, Flow[]> = {
  piano: [
    {
      id: 'default',
      name: 'Default Flow',
      facebook_page_id: '130724596952924',
      instagram_account_id: '17841404511284977',
      url: 'https://join-piano.hellosimply.com/start?utm_source=facebook&utm_medium={{adset.id}}&utm_campaign={{campaign.id}}&utm_content={{ad.name}}&placement={{placement}}&sitesource={{site_source_name}}&ad_id={{ad.id}}',
      primary_texts: [
        { id: 'default_1', text: 'Master the piano in no time! 🎶 Join millions of learners on Simply Piano and start playing today! 🎹✨' },
        { id: 'default_2', text: 'Learn piano together with Simply Piano!\nNow available with the new Family Plan' },
        { id: 'default_3', text: 'The must-have app for 2025, as recommended by Apple!' },
        { id: 'default_4', text: 'Learning piano through video tutorials? Discover Simply Piano, the #1 app to help you play with confidence and make real progress.' },
        { id: 'default_5', text: 'Simply Piano keeps kids motivated with playful lessons, songs, and rewarding milestones🎶.🎹' },
        { id: 'default_6', text: 'Help your child build piano skills through fun practice and motivating progress🎶.🎹' },
      ],
    },
    {
      id: 'ma',
      name: 'MA Flow',
      facebook_page_id: '978667585319817',
      instagram_account_id: '17841479023365212',
      url: 'https://join-piano.hellosimply.com/start?flow=ma&utm_source=facebook&utm_medium={{adset.id}}&utm_campaign={{campaign.id}}&utm_content={{ad.name}}&placement={{placement}}&sitesource={{site_source_name}}&ad_id={{ad.id}}',
      primary_texts: [
        { id: 'ma_1', text: 'Are they into art? Or is it music? With Simply, your child can explore both worlds and decide later.' },
        { id: 'ma_2', text: 'Our app adapts to your child, not the other way around. So they have all the power to learn by themselves in an engaging and exciting way.' },
        { id: 'ma_3', text: 'Your kid will be hooked from day one, with endless engaging content, challenges and prizes. Designed especially for kids 7-10.' },
      ],
    },
  ],
  guitar: [
    {
      id: 'default',
      name: 'Default Flow',
      facebook_page_id: '114002663580330',
      instagram_account_id: '17841438834706247',
      url: 'https://join-guitar.hellosimply.com/start?utm_source=facebook&utm_medium={{adset.id}}&utm_campaign={{campaign.id}}&utm_content={{ad.name}}&placement={{placement}}&sitesource={{site_source_name}}&ad_id={{ad.id}}',
      primary_texts: [
        { id: 'default_1', text: 'Stop guessing and start playing with confidence! 🎸 Simply Guitar listens as you play and gives you instant feedback, so you\'ll know when you\'re getting it right. Start learning today! ✨' },
        { id: 'default_2', text: 'That guitar you bought when you were feeling optimistic? It\'s still there. 🎸 Pick it up again with simple, step-by-step lessons that make starting feel easy. Your first song could be closer than you think ✨' },
        { id: 'default_3', text: 'You don\'t need hours of practice or years of experience. ⏱️🎶 Start with just a few minutes a day and learn to play songs you actually love—at your own pace.' },
        { id: 'default_4', text: 'Learning guitar on your own can feel confusing. Simply Guitar gives you a clear path, real-time feedback, and the confidence to keep going. 🎸 Start your musical journey today ✨' },
        { id: 'default_5', text: 'You didn\'t buy a guitar just to leave it in the corner. 👀🎸 Start where you are, learn at your own pace, and turn "one day" into the day you play your first song.' },
      ],
    },
  ],
  draw: [],
  sing: [],
};

const APP_PRIMARY_TEXT_OPTIONS: Record<FrontendAppDeployment, PrimaryTextOption[]> = {
  piano: [
    { id: 'default_1', text: 'Master the piano in no time! 🎶 Join millions of learners on Simply Piano and start playing today! 🎹✨', flow: 'default' },
    { id: 'default_2', text: 'Learn piano together with Simply Piano!\nNow available with the new Family Plan', flow: 'default' },
    { id: 'default_3', text: 'The must-have app for 2025, as recommended by Apple!', flow: 'default' },
    { id: 'default_4', text: 'Learning piano through video tutorials? Discover Simply Piano, the #1 app to help you play with confidence and make real progress.', flow: 'default' },
    { id: 'default_5', text: 'Simply Piano keeps kids motivated with playful lessons, songs, and rewarding milestones🎶.🎹', flow: 'default' },
    { id: 'default_6', text: 'Help your child build piano skills through fun practice and motivating progress🎶.🎹', flow: 'default' },
  ],
  guitar: [
    { id: 'default_1', text: 'Stop guessing and start playing with confidence! 🎸 Simply Guitar listens as you play and gives you instant feedback, so you\'ll know when you\'re getting it right. Start learning today! ✨', flow: 'default' },
    { id: 'default_2', text: 'That guitar you bought when you were feeling optimistic? It\'s still there. 🎸 Pick it up again with simple, step-by-step lessons that make starting feel easy. Your first song could be closer than you think ✨', flow: 'default' },
    { id: 'default_3', text: 'You don\'t need hours of practice or years of experience. ⏱️🎶 Start with just a few minutes a day and learn to play songs you actually love—at your own pace.', flow: 'default' },
    { id: 'default_4', text: 'Learning guitar on your own can feel confusing. Simply Guitar gives you a clear path, real-time feedback, and the confidence to keep going. 🎸 Start your musical journey today ✨', flow: 'default' },
    { id: 'default_5', text: 'You didn\'t buy a guitar just to leave it in the corner. 👀🎸 Start where you are, learn at your own pace, and turn "one day" into the day you play your first song.', flow: 'default' },
  ],
  draw: [],
  sing: [],
};

const APP_WEBSITE_URL: Record<FrontendAppDeployment, string> = {
  piano: 'https://join-piano.hellosimply.com/start?utm_source=facebook&utm_medium={{adset.id}}&utm_campaign={{campaign.id}}&utm_content={{ad.name}}&placement={{placement}}&sitesource={{site_source_name}}&ad_id={{ad.id}}',
  guitar: 'https://join-guitar.hellosimply.com/start?utm_source=facebook&utm_medium={{adset.id}}&utm_campaign={{campaign.id}}&utm_content={{ad.name}}&placement={{placement}}&sitesource={{site_source_name}}&ad_id={{ad.id}}',
  draw: '',
  sing: '',
};

const APP_HEADLINE: Record<FrontendAppDeployment, string> = {
  piano: 'Start Learning Today!',
  guitar: 'Start Learning Today!',
  draw: '',
  sing: '',
};

const appName = getFrontendAppDeployment();

const steps = ['Select Campaign & Ad Set', 'Configure Ad'];

const CreateMetaAdDialog: React.FC<CreateMetaAdDialogProps> = ({
  open,
  onClose,
  selectedAdIds,
}) => {
  // Stepper state
  const [activeStep, setActiveStep] = useState(0);

  // Meta account selection
  const [metaAccounts, setMetaAccounts] = useState<Array<{ key: string; label: string; ad_account_id: string }>>([]);
  const [selectedAccountKey, setSelectedAccountKey] = useState<string>('default');

  // Data loading states
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [adsets, setAdsets] = useState<AdSet[]>([]);
  const [flows, setFlows] = useState<Flow[]>(APP_FLOWS[appName]);
  const [primaryTextOptions, setPrimaryTextOptions] = useState<PrimaryTextOption[]>(APP_PRIMARY_TEXT_OPTIONS[appName]);
  const [defaultWebsiteUrl, setDefaultWebsiteUrl] = useState(APP_WEBSITE_URL[appName]);
  const [defaultHeadline, setDefaultHeadline] = useState(APP_HEADLINE[appName]);
  
  // Selected flow
  const [selectedFlow, setSelectedFlow] = useState<string>('default');
  
  // Conversion events and locations
  const [conversionEvents, setConversionEvents] = useState<string[]>(['PURCHASE']);
  const [priorityLocations, setPriorityLocations] = useState<LocationOption[]>([]);
  const [otherLocations, setOtherLocations] = useState<LocationOption[]>([]);
  const [defaultLocations, setDefaultLocations] = useState<string[]>(['US', 'CA', 'AU', 'GB']);
  
  const [isLoadingCampaigns, setIsLoadingCampaigns] = useState(false);
  const [isLoadingAdsets, setIsLoadingAdsets] = useState(false);
  
  // Cache adsets by campaign ID to avoid repeated API calls
  const [adsetsCache, setAdsetsCache] = useState<Record<string, AdSet[]>>({});

  // Step 1: Campaign selection
  const [campaignType, setCampaignType] = useState<'existing' | 'new'>('existing');
  const [selectedCampaign, setSelectedCampaign] = useState<Campaign | null>(null);
  
  // New campaign fields
  const [newCampaignName, setNewCampaignName] = useState('');
  const [defaultCampaignName, setDefaultCampaignName] = useState('');
  
  // Step 1: Ad Set selection
  const [adSetType, setAdSetType] = useState<'existing' | 'new'>('existing');
  const [selectedAdSet, setSelectedAdSet] = useState<AdSet | null>(null);
  
  // New ad set fields
  const [newAdsetName, setNewAdsetName] = useState('');
  const [newAdsetBudget, setNewAdsetBudget] = useState<number>(1000); // Default $1000
  const [newAdsetPerformanceGoal, setNewAdsetPerformanceGoal] = useState<MetaPerformanceGoal>(DEFAULT_PERFORMANCE_GOAL);
  const [newAdsetConversionEvent, setNewAdsetConversionEvent] = useState('PURCHASE');
  const [newAdsetLocations, setNewAdsetLocations] = useState<string[]>(['US', 'CA', 'AU', 'GB']);
  const [newAdsetAttributionModel, setNewAdsetAttributionModel] = useState<'standard' | 'incremental'>('standard');

  // Step 2: Ad configuration
  const [websiteUrl, setWebsiteUrl] = useState(APP_WEBSITE_URL[appName]);
  const [primaryTextOption, setPrimaryTextOption] = useState('1');
  const [customPrimaryText, setCustomPrimaryText] = useState('');
  const [headline, setHeadline] = useState(APP_HEADLINE[appName]);

  // Loading/error/success states
  const [isLoading, setIsLoading] = useState(false);
  const [isCreatingCampaign, setIsCreatingCampaign] = useState(false);
  const [isCreatingAdset, setIsCreatingAdset] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successResult, setSuccessResult] = useState<{
    adsCreated: number;
    adsFailed: number;
    successfulAds: Array<{ ad_id: string; ad_name: string }>;
    failedAds: Array<{ ad_name: string; error: string }>;
  } | null>(null);
  const [showSuccessSnackbar, setShowSuccessSnackbar] = useState(false);

  // Helper function to update ad set name preview (defined early for useEffect dependencies)
  const updateAdsetNamePreview = useCallback(async (campaignName: string) => {
    try {
      const response = await apiService.previewMetaAdsetName(campaignName);
      setNewAdsetName(response.adset_name);
    } catch (err) {
      console.error('Failed to preview adset name:', err);
      setNewAdsetName(campaignName);
    }
  }, []);

  // Fetch accounts + campaigns when dialog opens
  useEffect(() => {
    if (open) {
      apiService.getMetaAccounts()
        .then(data => {
          setMetaAccounts(data.accounts);
          if (data.accounts.length > 0 && !data.accounts.find(a => a.key === selectedAccountKey)) {
            setSelectedAccountKey(data.accounts[0].key);
          }
        })
        .catch(err => console.warn('Failed to load Meta accounts:', err));
      fetchCampaigns();
      fetchFlows();
      fetchPrimaryTextOptions();
      fetchConversionEventsAndLocations();
      fetchDefaultCampaignName();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  
  // Update URL and primary text options when flow changes
  useEffect(() => {
    const flow = flows.find(f => f.id === selectedFlow);
    if (flow) {
      setWebsiteUrl(flow.url);
      // Reset primary text to first option of the new flow
      if (flow.primary_texts.length > 0) {
        setPrimaryTextOption(flow.primary_texts[0].id);
      }
    }
  }, [selectedFlow, flows]);

  // Fetch adsets when campaign is selected
  useEffect(() => {
    if (selectedCampaign && campaignType === 'existing') {
      fetchAdsets(selectedCampaign.id);
    } else {
      setAdsets([]);
      setSelectedAdSet(null);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCampaign, campaignType]);

  // Auto-generate adset name when campaign name changes or when selecting existing campaign
  useEffect(() => {
    if (adSetType === 'new') {
      if (campaignType === 'new' && newCampaignName) {
        updateAdsetNamePreview(newCampaignName);
      } else if (campaignType === 'existing' && selectedCampaign) {
        updateAdsetNamePreview(selectedCampaign.name);
      }
    }
  }, [newCampaignName, campaignType, selectedCampaign, adSetType, updateAdsetNamePreview]);

  // Clear adset name when switching back to "Use Existing Ad Set"
  useEffect(() => {
    if (adSetType === 'existing') {
      setNewAdsetName('');
    }
  }, [adSetType]);

  // When account changes, clear and re-fetch campaigns/adsets
  const handleAccountChange = (newAccountKey: string) => {
    setSelectedAccountKey(newAccountKey);
    setCampaigns([]);
    setAdsets([]);
    setAdsetsCache({});
    setSelectedCampaign(null);
    setSelectedAdSet(null);
    fetchCampaigns(newAccountKey);
  };

  // Reset state when dialog opens/closes
  useEffect(() => {
    if (open) {
      setActiveStep(0);
      setCampaignType('existing');
      setSelectedCampaign(null);
      setAdSetType('existing');
      setSelectedAdSet(null);
      setNewCampaignName(defaultCampaignName);
      setNewAdsetName('');
      setNewAdsetBudget(1000);
      setNewAdsetPerformanceGoal(DEFAULT_PERFORMANCE_GOAL);
      setNewAdsetConversionEvent('PURCHASE');
      setNewAdsetLocations(defaultLocations);
      setNewAdsetAttributionModel('standard');
      setSelectedFlow('default');
      setWebsiteUrl(defaultWebsiteUrl);
      setPrimaryTextOption('default_1');
      setCustomPrimaryText('');
      setHeadline(defaultHeadline);
      setError(null);
      setSuccessResult(null);
    }
  }, [open, defaultWebsiteUrl, defaultHeadline, defaultCampaignName, defaultLocations]);

  const fetchCampaigns = async (accountKey?: string) => {
    const acctKey = accountKey || selectedAccountKey;
    const CACHE_KEY = `meta_campaigns_cache_${acctKey}`;
    const CACHE_DURATION = 2 * 60 * 1000;
    
    try {
      const cached = localStorage.getItem(CACHE_KEY);
      if (cached) {
        const { data, timestamp } = JSON.parse(cached);
        if (Date.now() - timestamp < CACHE_DURATION) {
          setCampaigns(data);
          return;
        }
      }
    } catch (e) {}
    
    setIsLoadingCampaigns(true);
    setError(null);
    try {
      const response = await apiService.getMetaCampaigns(30, acctKey);
      setCampaigns(response.campaigns);
      localStorage.setItem(CACHE_KEY, JSON.stringify({ data: response.campaigns, timestamp: Date.now() }));
    } catch (err: any) {
      if (err.message.includes('rate limit') || err.message.includes('Too Many API Calls')) {
        setError('Meta API rate limit reached. Please wait 5-10 minutes and try again.');
      } else {
        setError(`Failed to load campaigns: ${err.message}`);
      }
    } finally {
      setIsLoadingCampaigns(false);
    }
  };

  const fetchFlows = async () => {
    try {
      const response = await apiService.getMetaFlows();
      if (response.flows && response.flows.length > 0) {
        setFlows(response.flows);
        // Set default flow's URL
        const defaultFlow = response.flows.find(f => f.id === 'default') || response.flows[0];
        if (defaultFlow) {
          setDefaultWebsiteUrl(defaultFlow.url);
        }
      }
    } catch (err: any) {
      console.error('Failed to fetch flows, using fallback:', err);
      // Keep using FALLBACK_FLOWS
    }
  };

  const fetchAdsets = async (campaignId: string) => {
    const cacheKey = `${campaignId}_${selectedAccountKey}`;
    if (adsetsCache[cacheKey]) {
      setAdsets(adsetsCache[cacheKey]);
      setSelectedAdSet(null);
      return;
    }
    
    const CACHE_KEY = `meta_adsets_${cacheKey}`;
    const CACHE_DURATION = 2 * 60 * 1000;
    
    try {
      const cached = localStorage.getItem(CACHE_KEY);
      if (cached) {
        const { data, timestamp } = JSON.parse(cached);
        if (Date.now() - timestamp < CACHE_DURATION) {
          setAdsets(data);
          setAdsetsCache(prev => ({ ...prev, [cacheKey]: data }));
          setSelectedAdSet(null);
          return;
        }
      }
    } catch (e) {}
    
    setIsLoadingAdsets(true);
    setSelectedAdSet(null);
    try {
      const response = await apiService.getMetaAdsets(campaignId, 15, selectedAccountKey);
      setAdsets(response.adsets);
      setAdsetsCache(prev => ({ ...prev, [cacheKey]: response.adsets }));
      localStorage.setItem(CACHE_KEY, JSON.stringify({ data: response.adsets, timestamp: Date.now() }));
    } catch (err: any) {
      if (err.message.includes('rate limit') || err.message.includes('Too Many API Calls')) {
        setError('Meta API rate limit reached. Please wait 5-10 minutes and try again.');
      } else {
        setError(`Failed to load ad sets: ${err.message}`);
      }
    } finally {
      setIsLoadingAdsets(false);
    }
  };

  const fetchPrimaryTextOptions = async () => {
    try {
      const response = await apiService.getMetaPrimaryTextOptions();
      setPrimaryTextOptions(response.options);
      setDefaultWebsiteUrl(response.default_website_url);
      setDefaultHeadline(response.default_headline);
      setWebsiteUrl(response.default_website_url);
      setHeadline(response.default_headline);
    } catch (err) {
      console.error('Failed to fetch primary text options:', err);
    }
  };

  const fetchConversionEventsAndLocations = async () => {
    try {
      const [eventsResponse, locationsResponse] = await Promise.all([
        apiService.getMetaConversionEvents(),
        apiService.getMetaLocations(),
      ]);
      
      setConversionEvents(eventsResponse.events);
      setPriorityLocations(locationsResponse.priority);
      setOtherLocations(locationsResponse.other);
      setDefaultLocations(locationsResponse.default);
      setNewAdsetLocations(locationsResponse.default);
    } catch (err) {
      console.error('Failed to fetch conversion events/locations:', err);
    }
  };

  const fetchDefaultCampaignName = async () => {
    try {
      const response = await apiService.getMetaDefaultCampaignName();
      setDefaultCampaignName(response.default_name);
      setNewCampaignName(response.default_name);
    } catch (err) {
      console.error('Failed to fetch default campaign name:', err);
    }
  };

  const handleNext = () => {
    if (activeStep === 0) {
      // Validate campaign selection
      if (campaignType === 'existing' && !selectedCampaign) {
        setError('Please select a campaign');
        return;
      }
      if (campaignType === 'new' && !newCampaignName.trim()) {
        setError('Please enter a campaign name');
        return;
      }
      
      // Validate ad set selection
      if (adSetType === 'existing' && !selectedAdSet) {
        setError('Please select an ad set');
        return;
      }
      if (adSetType === 'new' && !newAdsetName.trim()) {
        setError('Please enter an ad set name');
        return;
      }
    }
    setError(null);
    setActiveStep((prev) => prev + 1);
  };

  const handleBack = () => {
    setError(null);
    setActiveStep((prev) => prev - 1);
  };

  const handleCreate = async () => {
    setIsLoading(true);
    setError(null);

    try {
      let campaignId = selectedCampaign?.id;
      let adsetId = selectedAdSet?.id;

      // Create campaign if needed
      if (campaignType === 'new') {
        setIsCreatingCampaign(true);
        const campaignResult = await apiService.createMetaCampaign({
          name: newCampaignName,
          objective: 'OUTCOME_SALES',
          account_key: selectedAccountKey,
        });
        campaignId = campaignResult.campaign.id;
        setIsCreatingCampaign(false);
      }

      // Create ad set if needed
      if (adSetType === 'new' && campaignId) {
        setIsCreatingAdset(true);
        const adsetResult = await apiService.createMetaAdset({
          campaign_id: campaignId,
          name: newAdsetName,
          daily_budget: newAdsetBudget * 100,
          billing_event: 'IMPRESSIONS',
          optimization_goal: newAdsetPerformanceGoal,
          conversion_event: newAdsetConversionEvent,
          locations: newAdsetLocations,
          attribution_model: newAdsetAttributionModel,
          account_key: selectedAccountKey,
        });
        adsetId = adsetResult.adset.id;
        setIsCreatingAdset(false);
      }

      if (!campaignId || !adsetId) {
        throw new Error('Failed to get campaign or ad set ID');
      }

      // Get the final primary text
      const currentFlow = flows.find(f => f.id === selectedFlow);
      const finalPrimaryText = primaryTextOption === 'custom' 
        ? customPrimaryText 
        : currentFlow?.primary_texts.find(opt => opt.id === primaryTextOption)?.text 
          || primaryTextOptions.find(opt => opt.id === primaryTextOption)?.text 
          || '';

      // Call API to create Meta ads with flow-specific page/instagram IDs
      const result = await apiService.createMetaAd({
        ad_ids: selectedAdIds,
        campaign_id: campaignId,
        adset_id: adsetId,
        website_url: websiteUrl,
        primary_text: finalPrimaryText,
        headline: headline,
        page_id: currentFlow?.facebook_page_id,
        instagram_id: currentFlow?.instagram_account_id,
        account_key: selectedAccountKey,
      });

      setSuccessResult({
        adsCreated: result.summary.ads_created,
        adsFailed: result.summary.ads_failed,
        successfulAds: result.successful_ads.map(ad => ({ ad_id: ad.ad_id, ad_name: ad.ad_name })),
        failedAds: result.failed_ads.map(ad => ({ ad_name: ad.ad_name, error: ad.error })),
      });

      if (result.summary.ads_failed === 0) {
        setShowSuccessSnackbar(true);
        onClose();
      }
      
    } catch (err: any) {
      setError(err.message || 'Failed to create Meta ad');
    } finally {
      setIsLoading(false);
      setIsCreatingCampaign(false);
      setIsCreatingAdset(false);
    }
  };

  const allLocations = [...priorityLocations, ...otherLocations];

  const renderStep1 = () => (
    <Box sx={{ mt: 2 }}>
      {/* Account Selection */}
      {metaAccounts.length > 1 && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
            Meta Ad Account
          </Typography>
          <FormControl fullWidth size="small">
            <Select
              value={selectedAccountKey}
              onChange={(e) => handleAccountChange(e.target.value)}
            >
              {metaAccounts.map(account => (
                <MenuItem key={account.key} value={account.key}>
                  {account.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      )}

      {/* Campaign Selection */}
      <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
        Campaign
      </Typography>
      
      <FormControl component="fieldset" sx={{ mb: 2, width: '100%' }}>
        <RadioGroup
          value={campaignType}
          onChange={(e) => {
            setCampaignType(e.target.value as 'existing' | 'new');
            // Reset ad set type when switching campaign type
            if (e.target.value === 'new') {
              setAdSetType('new');
            }
          }}
        >
          <FormControlLabel value="existing" control={<Radio />} label="Use Existing Campaign" />
          <FormControlLabel value="new" control={<Radio />} label="Create New Campaign" />
        </RadioGroup>
      </FormControl>

      {campaignType === 'existing' ? (
        <Autocomplete
          options={campaigns}
          getOptionLabel={(option) => `${option.name} (${option.status})`}
          value={selectedCampaign}
          onChange={(_, newValue) => setSelectedCampaign(newValue)}
          loading={isLoadingCampaigns}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Select Campaign"
              placeholder="Search campaigns..."
              fullWidth
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {isLoadingCampaigns ? <CircularProgress color="inherit" size={20} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
          renderOption={(props, option) => (
            <li {...props}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
                <Typography>{option.name}</Typography>
                <Chip label={option.status} size="small" color={option.status === 'ACTIVE' ? 'success' : 'default'} />
              </Box>
            </li>
          )}
          sx={{ mb: 3 }}
          noOptionsText={isLoadingCampaigns ? "Loading campaigns..." : "No campaigns found"}
        />
      ) : (
        <TextField
          label="Campaign Name"
          value={newCampaignName}
          onChange={(e) => setNewCampaignName(e.target.value)}
          fullWidth
          sx={{ mb: 3 }}
          helperText="Campaign will be created with PAUSED status"
        />
      )}

      {/* Ad Set Selection */}
      <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
        Ad Set
      </Typography>
      
      <FormControl component="fieldset" sx={{ mb: 2, width: '100%' }}>
        <RadioGroup
          value={adSetType}
          onChange={(e) => setAdSetType(e.target.value as 'existing' | 'new')}
        >
          <FormControlLabel 
            value="existing" 
            control={<Radio />} 
            label="Use Existing Ad Set"
            disabled={campaignType === 'new'}
          />
          <FormControlLabel value="new" control={<Radio />} label="Create New Ad Set" />
        </RadioGroup>
      </FormControl>

      {adSetType === 'existing' ? (
        <Autocomplete
          options={adsets}
          getOptionLabel={(option) => `${option.name} (${option.status})`}
          value={selectedAdSet}
          onChange={(_, newValue) => setSelectedAdSet(newValue)}
          disabled={!selectedCampaign}
          loading={isLoadingAdsets}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Select Ad Set"
              placeholder={selectedCampaign ? "Search ad sets..." : "Select a campaign first"}
              fullWidth
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {isLoadingAdsets ? <CircularProgress color="inherit" size={20} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
          renderOption={(props, option) => (
            <li {...props}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
                <Typography>{option.name}</Typography>
                <Chip label={option.status} size="small" color={option.status === 'ACTIVE' ? 'success' : 'default'} />
              </Box>
            </li>
          )}
          noOptionsText={!selectedCampaign ? "Select a campaign first" : isLoadingAdsets ? "Loading ad sets..." : "No ad sets found"}
        />
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {/* Ad Set Name */}
          <TextField
            label="Ad Set Name"
            value={newAdsetName}
            onChange={(e) => setNewAdsetName(e.target.value)}
            fullWidth
            helperText="Auto-generated from campaign name with today's Israel date"
          />

          {/* Daily Budget */}
          <TextField
            label="Daily Budget"
            type="number"
            value={newAdsetBudget}
            onChange={(e) => setNewAdsetBudget(Number(e.target.value))}
            fullWidth
            InputProps={{
              startAdornment: <InputAdornment position="start">$</InputAdornment>,
            }}
          />

          {/* Performance Goal */}
          <FormControl fullWidth>
            <InputLabel>Performance Goal</InputLabel>
            <Select
              value={newAdsetPerformanceGoal}
              onChange={(e) => setNewAdsetPerformanceGoal(e.target.value as MetaPerformanceGoal)}
              label="Performance Goal"
            >
              {PERFORMANCE_GOAL_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  <ListItemText primary={option.label} secondary={option.description} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Conversion Event */}
          <FormControl fullWidth>
            <InputLabel>Conversion Event</InputLabel>
            <Select
              value={newAdsetConversionEvent}
              onChange={(e) => setNewAdsetConversionEvent(e.target.value)}
              label="Conversion Event"
            >
              {conversionEvents.map((event) => (
                <MenuItem key={event} value={event}>{event}</MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Locations Multi-Select */}
          <FormControl fullWidth>
            <InputLabel>Target Locations</InputLabel>
            <Select
              multiple
              value={newAdsetLocations}
              onChange={(e) => setNewAdsetLocations(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)}
              input={<OutlinedInput label="Target Locations" />}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((code) => {
                    const loc = allLocations.find(l => l.code === code);
                    return <Chip key={code} label={loc?.name || code} size="small" />;
                  })}
                </Box>
              )}
            >
              <MenuItem disabled sx={{ fontWeight: 'bold', bgcolor: 'grey.100' }}>Priority Countries</MenuItem>
              {priorityLocations.map((loc) => (
                <MenuItem key={loc.code} value={loc.code}>
                  <Checkbox checked={newAdsetLocations.includes(loc.code)} />
                  <ListItemText primary={loc.name} />
                </MenuItem>
              ))}
              <MenuItem disabled sx={{ fontWeight: 'bold', bgcolor: 'grey.100', mt: 1 }}>Other Countries</MenuItem>
              {otherLocations.map((loc) => (
                <MenuItem key={loc.code} value={loc.code}>
                  <Checkbox checked={newAdsetLocations.includes(loc.code)} />
                  <ListItemText primary={loc.name} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Attribution Model */}
          <FormControl fullWidth>
            <Typography variant="body2" fontWeight="bold" sx={{ mb: 0.5 }}>
              Attribution Model
            </Typography>
            <RadioGroup
              row
              value={newAdsetAttributionModel}
              onChange={(e) => setNewAdsetAttributionModel(e.target.value as 'standard' | 'incremental')}
            >
              <FormControlLabel value="standard" control={<Radio />} label="Standard" />
              <FormControlLabel value="incremental" control={<Radio />} label="Incremental" />
            </RadioGroup>
            <Typography variant="caption" color="text.secondary">
              {newAdsetAttributionModel === 'incremental'
                ? 'Uses holdout testing to measure only conversions caused by the ad'
                : 'Counts all conversions within the attribution window'}
            </Typography>
          </FormControl>
        </Box>
      )}
    </Box>
  );

  // Get current flow's primary texts for the dropdown
  const currentFlow = flows.find(f => f.id === selectedFlow);
  const currentFlowPrimaryTexts = currentFlow?.primary_texts || [];

  const renderStep2 = () => (
    <Box sx={{ mt: 2 }}>
      {/* Flow Selection */}
      <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
        Flow
      </Typography>
      <FormControl component="fieldset" sx={{ mb: 3, width: '100%' }}>
        <RadioGroup
          value={selectedFlow}
          onChange={(e) => setSelectedFlow(e.target.value)}
        >
          {flows.map((flow) => (
            <Box key={flow.id} sx={{ mb: 1 }}>
              <FormControlLabel
                value={flow.id}
                control={<Radio />}
                label={
                  <Box>
                    <Typography variant="body1" fontWeight="medium">
                      {flow.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      FB: {flow.facebook_page_id} | IG: {flow.instagram_account_id}
                    </Typography>
                  </Box>
                }
              />
            </Box>
          ))}
        </RadioGroup>
      </FormControl>

      {/* Website URL */}
      <TextField
        label="Website URL"
        value={websiteUrl}
        onChange={(e) => setWebsiteUrl(e.target.value)}
        fullWidth
        multiline
        rows={2}
        sx={{ mb: 3 }}
        helperText="The destination URL when users click on your ad (auto-filled based on flow, editable)"
      />

      {/* Primary Text */}
      <FormControl fullWidth sx={{ mb: 2 }}>
        <InputLabel>Primary Text</InputLabel>
        <Select
          value={primaryTextOption}
          onChange={(e) => setPrimaryTextOption(e.target.value)}
          label="Primary Text"
        >
          {currentFlowPrimaryTexts.length > 0 && (
            <MenuItem disabled sx={{ fontWeight: 'bold', bgcolor: 'grey.100' }}>
              {currentFlow?.name} Options
            </MenuItem>
          )}
          {currentFlowPrimaryTexts.map((option) => (
            <MenuItem key={option.id} value={option.id}>
              <Typography noWrap sx={{ maxWidth: 500 }}>
                {option.text.length > 80 ? `${option.text.substring(0, 80)}...` : option.text}
              </Typography>
            </MenuItem>
          ))}
          <MenuItem value="custom"><em>Custom text...</em></MenuItem>
        </Select>
      </FormControl>

      {primaryTextOption === 'custom' && (
        <TextField
          label="Custom Primary Text"
          value={customPrimaryText}
          onChange={(e) => setCustomPrimaryText(e.target.value)}
          fullWidth
          multiline
          rows={3}
          sx={{ mb: 3 }}
          placeholder="Enter your custom ad text..."
        />
      )}

      {primaryTextOption !== 'custom' && (
        <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
          <Typography variant="caption" color="text.secondary">Selected Primary Text:</Typography>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
            {currentFlowPrimaryTexts.find(opt => opt.id === primaryTextOption)?.text || 
             primaryTextOptions.find(opt => opt.id === primaryTextOption)?.text}
          </Typography>
        </Box>
      )}

      {/* Headline */}
      <TextField
        label="Headline"
        value={headline}
        onChange={(e) => setHeadline(e.target.value)}
        fullWidth
        sx={{ mb: 2 }}
        helperText="The headline that appears below the ad image/video"
      />

      {/* Summary */}
      <Box sx={{ mt: 3, p: 2, bgcolor: 'primary.light', borderRadius: 1, color: 'primary.contrastText' }}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>Summary</Typography>
        <Typography variant="body2">
          Flow: <strong>{currentFlow?.name}</strong>
        </Typography>
        <Typography variant="body2">
          Creating ads from <strong>{selectedAdIds.length}</strong> selected asset{selectedAdIds.length > 1 ? 's' : ''}
        </Typography>
        <Typography variant="body2">
          Campaign: <strong>{campaignType === 'new' ? `${newCampaignName} (NEW)` : selectedCampaign?.name}</strong>
        </Typography>
        <Typography variant="body2">
          Ad Set: <strong>{adSetType === 'new' ? `${newAdsetName} (NEW)` : selectedAdSet?.name}</strong>
        </Typography>
        {adSetType === 'new' && (
          <>
            <Typography variant="body2">Daily Budget: <strong>${newAdsetBudget}</strong></Typography>
            <Typography variant="body2">Performance Goal: <strong>{getPerformanceGoalLabel(newAdsetPerformanceGoal)}</strong></Typography>
            <Typography variant="body2">Locations: <strong>{newAdsetLocations.join(', ')}</strong></Typography>
            <Typography variant="body2">Attribution: <strong>{newAdsetAttributionModel === 'incremental' ? 'Incremental' : 'Standard'}</strong></Typography>
          </>
        )}
      </Box>

      {/* Success/Failure Result */}
      {successResult && (
        <Box sx={{ mt: 3 }}>
          {successResult.adsCreated > 0 && (
            <Alert severity="success" sx={{ mb: 2 }}>
              Successfully created {successResult.adsCreated} ad{successResult.adsCreated > 1 ? 's' : ''}!
            </Alert>
          )}
          {successResult.adsFailed > 0 && (
            <Alert severity="error" sx={{ mb: 2 }}>
              Failed to create {successResult.adsFailed} ad{successResult.adsFailed > 1 ? 's' : ''}:
              <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px' }}>
                {successResult.failedAds.map((ad, idx) => (
                  <li key={idx}>{ad.ad_name}: {ad.error}</li>
                ))}
              </ul>
            </Alert>
          )}
        </Box>
      )}
    </Box>
  );

  const getLoadingText = () => {
    if (isCreatingCampaign) return 'Creating campaign...';
    if (isCreatingAdset) return 'Creating ad set...';
    return 'Creating ads...';
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={() => {}}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { minHeight: '60vh' } }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <MetaIcon sx={{ color: '#1877F2' }} />
          Create Meta Ad
        </DialogTitle>

        <DialogContent>
          <Stepper activeStep={activeStep} sx={{ pt: 2, pb: 3 }}>
            {steps.map((label) => (
              <Step key={label}><StepLabel>{label}</StepLabel></Step>
            ))}
          </Stepper>

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          {activeStep === 0 && renderStep1()}
          {activeStep === 1 && renderStep2()}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={onClose} disabled={isLoading}>
            {successResult ? 'Close' : 'Cancel'}
          </Button>
          
          {activeStep > 0 && !successResult && (
            <Button onClick={handleBack} disabled={isLoading}>Back</Button>
          )}

          {activeStep < steps.length - 1 ? (
            <Button variant="contained" onClick={handleNext} disabled={isLoading || isLoadingCampaigns || isLoadingAdsets}>
              Next
            </Button>
          ) : !successResult ? (
            <Button
              variant="contained"
              onClick={handleCreate}
              disabled={isLoading}
              startIcon={isLoading ? <CircularProgress size={16} color="inherit" /> : <MetaIcon />}
              sx={{ backgroundColor: '#1877F2', '&:hover': { backgroundColor: '#166FE5' } }}
            >
              {isLoading ? getLoadingText() : 'Create Ad'}
            </Button>
          ) : null}
        </DialogActions>
      </Dialog>

      <Snackbar
        open={showSuccessSnackbar}
        autoHideDuration={6000}
        onClose={() => setShowSuccessSnackbar(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setShowSuccessSnackbar(false)} severity="success" sx={{ width: '100%' }} icon={<SuccessIcon />}>
          Meta ads created successfully!
        </Alert>
      </Snackbar>
    </>
  );
};

export default CreateMetaAdDialog;
